/**
 * BB-14d2c2 -- agent-facing copy for the audio lifecycle.
 *
 * **No provider vocabulary reaches an agent.** Not SIP, not REGISTER,
 * not INVITE, not ConfBridge, not Asterisk, not VICIdial, not a peer
 * name, not a conference extension, not a lease generation. An agent
 * needs to know whether audio works and what to do about it; every
 * other word is an internal detail that would only invite a support
 * call about a system the agent cannot see.
 *
 * Every message is keyed by a bounded reason. An unrecognised reason
 * falls through to the generic line rather than being rendered, so a
 * new backend reason can never surface raw text in the Workspace.
 */

const MESSAGES = Object.freeze({
  // Microphone -- the agent can act on all three.
  microphone_denied:
    'Microphone access is blocked. Allow microphone access for this site in your browser, then retry.',
  microphone_unavailable:
    'No working microphone was found. Connect a microphone or headset, then retry.',
  microphone_unsupported:
    'This browser cannot use your microphone here. Try a current version of Chrome or Edge.',
  microphone_failed: 'Your microphone could not be started. Check your headset, then retry.',

  // Identity refusals. Each has a different remedy, so none are merged.
  already_active:
    'Audio is already connected in another tab or window. Close the other one, then retry.',
  lease_not_owned: 'Your campaign session is no longer active. Log in to the campaign again.',
  not_configured: 'Audio is not set up for your account yet. Contact your supervisor.',
  media_not_configured: 'Audio is not set up for your account yet. Contact your supervisor.',
  connection_not_found: 'Audio is not set up for your account yet. Contact your supervisor.',
  agent_identity_not_found: 'Audio is not set up for your account yet. Contact your supervisor.',
  agent_identity_connection_mismatch:
    'Audio is not set up for your account yet. Contact your supervisor.',
  feature_disabled: 'Audio is not available right now.',

  // Readiness. `lease_superseded` is the one an agent most needs
  // stated plainly, because it happens when they open a second tab.
  lease_superseded:
    'Your campaign session was taken over by another tab or window. Use that one, or log in to the campaign again here.',
  identity_expired: 'Your audio session expired. Retry to reconnect.',
  no_active_identity: 'Audio is not connected. Retry to reconnect.',
  no_session: 'Your campaign session is no longer active. Log in to the campaign again.',
  conference_empty: 'Audio could not be connected. Retry to reconnect.',
  observation_stale: 'Audio could not be confirmed. Retry to reconnect.',
  provider_unavailable: 'Audio is temporarily unavailable. Retry in a moment.',

  // Lifecycle.
  audio_timeout: 'Audio could not be connected in time. Retry to reconnect.',
  registration_failed: 'Audio could not be connected. Retry to reconnect.',
  invite_failed: 'Audio could not be connected. Retry to reconnect.',
  conference_not_configured: 'Audio is not set up for your account yet. Contact your supervisor.',
  bootstrap_failed: 'Audio could not be started. Retry to reconnect.',
  media_leg_ended: 'Your audio disconnected. Retry to reconnect.',
});

const GENERIC = 'Audio could not be connected. Retry to reconnect.';

export function audioFailureMessage(reason) {
  if (typeof reason !== 'string') return GENERIC;
  return MESSAGES[reason] || GENERIC;
}

/** Shown when the browser refused to play audio without a gesture. */
export const ENABLE_AUDIO_PROMPT =
  'Your browser blocked audio playback. Select Enable Audio to hear calls.';

/** Shown when audio drops after the agent was already ready. */
export const AUDIO_LOST_NOTICE =
  'Your audio disconnected. You will not hear a call until it reconnects.';

/**
 * Shown when audio dropped after being ready *and* the agent has to act
 * to get it back.
 *
 * `AUDIO_LOST_NOTICE` promises a reconnection that happens on its own,
 * which is true while the server can still recover the leg. It is not
 * true once the media identity has expired: nothing reconnects without
 * the retry lifecycle, so the copy names the control instead of
 * predicting an event that will not occur.
 */
export const AUDIO_RECOVERY_NOTICE =
  'Your audio disconnected. Select Retry Audio to reconnect.';

export default audioFailureMessage;
